import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import apiClient from '../services/apiClient'
import { TrainingSessionFormData } from './useTrainingSessionForm'

type CreateTrainingSessionData = Omit<TrainingSessionFormData, 'id' | 'createdAt' | 'updatedAt'>
type UpdateTrainingSessionData = Omit<TrainingSessionFormData, 'coachId' | 'createdAt' | 'updatedAt'> & {
    id: number
}
type DuplicateTrainingSessionData = {
    sessionId: number
    targetGroupId?: number
}

interface UseTrainingSessionMutationsProps {
    user: { coachData: { id: number } } | null
    onSuccess: () => void
    onSettled: () => void
}

export const useTrainingSessionMutations = ({ user, onSuccess, onSettled }: UseTrainingSessionMutationsProps) => {
    const { t } = useTranslation()

    const createMutation = useMutation<unknown, AxiosError<{ message?: string }>, CreateTrainingSessionData>({
        mutationFn: async (data) => {
            if (!user) throw new Error('User not authenticated')
            const response = await apiClient.post<unknown>('/training-sessions', data, {
                params: { role: 'ROLE_COACH' },
            })

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to create training session')
            }

            return response.data
        },
        onSuccess: () => {
            toast.success(t('coach.trainingSessions.createdSuccess'))
            onSuccess()
        },
        onError: (error) => {
            console.error('Error creating training session:', error)
            const errorMessage = error.response?.data?.message ?? (error.message || t('common.errorOccurred'))
            toast.error(typeof errorMessage === 'string' ? errorMessage : t('common.errorOccurred'))
        },
        onSettled,
    })

    const updateMutation = useMutation<unknown, AxiosError<{ message?: string }>, UpdateTrainingSessionData>({
        mutationFn: async (data) => {
            if (!user) throw new Error('User not authenticated')
            const response = await apiClient.patch<unknown>(`/training-sessions/${data.id}`, data, {
                params: { role: 'ROLE_COACH' },
            })

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to update training session')
            }

            return response.data
        },
        onSuccess: () => {
            toast.success(t('coach.trainingSessions.updatedSuccess'))
            onSuccess()
        },
        onError: (error) => {
            console.error('Error updating training session:', error)
            const errorMessage = error.response?.data?.message ?? (error.message || t('common.errorOccurred'))
            toast.error(typeof errorMessage === 'string' ? errorMessage : t('common.errorOccurred'))
        },
        onSettled,
    })

    const duplicateMutation = useMutation<unknown, AxiosError<{ message?: string }>, DuplicateTrainingSessionData>({
        mutationFn: async (data) => {
            if (!user) throw new Error('User not authenticated')
            const response = await apiClient.post<unknown>(
                `/training-sessions/${data.sessionId}/duplicate`,
                {
                    groupId: data.targetGroupId,
                },
                {
                    params: { role: 'ROLE_COACH' },
                },
            )

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to duplicate training session')
            }

            return response.data
        },
        onSuccess: () => {
            toast.success(t('coach.trainingSessions.duplicateSuccess'))
            onSuccess()
        },
        onError: (error) => {
            console.error('Error duplicating training session:', error)
            const errorMessage = error.response?.data?.message ?? (error.message || t('common.errorOccurred'))
            toast.error(typeof errorMessage === 'string' ? errorMessage : t('common.errorOccurred'))
        },
        onSettled,
    })

    const submitTrainingSession = async (formData: TrainingSessionFormData, sessionId?: number) => {
        const submissionData = {
            ...formData,
            order: +formData.order,
            groupId: formData.groupId ? +formData.groupId : undefined,
            steps: formData.steps,
        }

        if (sessionId) {
            const updateData: UpdateTrainingSessionData = {
                ...submissionData,
                id: sessionId,
                description: submissionData.description !== undefined ? submissionData.description.trim() : undefined,
                steps: submissionData.steps,
            }
            await updateMutation.mutateAsync(updateData)
        } else {
            const cleanData: CreateTrainingSessionData = {
                ...submissionData,
                description: submissionData.description !== undefined ? submissionData.description.trim() : undefined,
                steps: submissionData.steps,
            }
            await createMutation.mutateAsync(cleanData)
        }
    }

    const duplicateTrainingSession = async (sessionId: number, targetGroupId?: number) => {
        const duplicateData: DuplicateTrainingSessionData = {
            sessionId,
            targetGroupId,
        }
        await duplicateMutation.mutateAsync(duplicateData)
    }

    return {
        createMutation,
        updateMutation,
        duplicateMutation,
        submitTrainingSession,
        duplicateTrainingSession,
        isLoading: createMutation.isPending || updateMutation.isPending || duplicateMutation.isPending,
    }
}
